/**
 * CP-137: shopper-facing place / title casing display normalize.
 *
 * Display-only: title-case words, preserve ampersands, preserve known
 * acronyms (ABC, FL, …). Dotted geo initialisms (B.C., U.K.) are upcased
 * whole. Two-letter state/postal codes are only forced uppercase when
 * trailing ("Miami FL") or already ALL-CAPS in the input ("FL Keys") — so
 * "La Spezia" stays "La Spezia", not "LA Spezia".
 * Does not invent or rename destinations; filter matching stays on ids.
 */

/** Always-upcase cruise / geo acronyms (safe in any position). */
const ALWAYS_ACRONYMS = new Set([
  'ABC',
  'UK', 'USA', 'UAE', 'EU', 'NZ', 'AU',
  'NYC', 'SF', 'DC',
]);

/** US state / territory postal codes — trailing or already-ALL-CAPS only. */
const STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
  'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
  'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'PR', 'VI', 'GU', 'AS', 'MP',
  'US',
]);

/**
 * @param {string} core
 * @param {{ preserveAllCapsTokens?: boolean, isTrailing?: boolean }} [opts]
 * @returns {string}
 */
/** Dotted geo initialism cores: B.C / B.C. / U.K / U.K. */
const DOTTED_GEO_INITIALISM = /^([A-Za-z]\.)+[A-Za-z]\.?$/;

function formatToken(core, opts = {}) {
  if (!core) return core;
  const { preserveAllCapsTokens = false, isTrailing = false } = opts;
  const upper = core.toUpperCase();
  const wasAllCaps = /^[A-Z0-9]+$/.test(core) && /[A-Z]/.test(core);

  // Dotted geo initialisms: "B.c" → "B.C" (trail punctuation kept by caller).
  if (DOTTED_GEO_INITIALISM.test(core)) return upper;

  if (ALWAYS_ACRONYMS.has(upper)) return upper;

  // State codes: trailing ("Miami FL") or already ALL-CAPS ("FL Keys").
  if (STATE_CODES.has(upper) && (isTrailing || wasAllCaps)) return upper;

  // Mixed-case inputs may mark other short acronyms in ALL CAPS.
  if (
    preserveAllCapsTokens
    && wasAllCaps
    && core.length >= 2
    && core.length <= 4
  ) {
    return upper;
  }

  return core
    .split(/(-)/)
    .map((seg) => {
      if (seg === '-' || !seg) return seg;
      if (DOTTED_GEO_INITIALISM.test(seg)) return seg.toUpperCase();
      const segUpper = seg.toUpperCase();
      if (ALWAYS_ACRONYMS.has(segUpper)) return segUpper;
      const segWasAllCaps = /^[A-Z0-9]+$/.test(seg) && /[A-Z]/.test(seg);
      if (STATE_CODES.has(segUpper) && (isTrailing || segWasAllCaps)) return segUpper;
      if (
        preserveAllCapsTokens
        && segWasAllCaps
        && seg.length >= 2
        && seg.length <= 4
      ) {
        return segUpper;
      }
      const lower = seg.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

/**
 * Display-normalize a place or cruise title string.
 * Examples: "TURKS & CAICOS" → "Turks & Caicos", "abc islands" → "ABC Islands",
 * "miami fl" → "Miami FL". Empty / nullish → "".
 * @param {*} value
 * @returns {string}
 */
export function formatPlaceTitle(value) {
  if (value == null) return '';
  const raw = String(value).trim().replace(/\s+/g, ' ');
  if (!raw) return '';

  // Only preserve incidental ALL-CAPS tokens when the whole string is mixed-case.
  const preserveAllCapsTokens = /[a-z]/.test(raw) && /[A-Z]/.test(raw);

  const parts = raw.split(/(\s+|&)/);
  // Last non-separator part index (for trailing state-code detection).
  let lastWordIdx = -1;
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const p = parts[i];
    if (p && !/^\s+$/.test(p) && p !== '&') {
      lastWordIdx = i;
      break;
    }
  }

  return parts
    .map((part, index) => {
      if (!part) return part;
      if (/^\s+$/.test(part)) return part;
      if (part === '&') return '&';

      const m = part.match(/^([^A-Za-z0-9]*)(.*?)([^A-Za-z0-9]*)$/);
      const lead = m ? m[1] : '';
      const core = m ? m[2] : part;
      const trail = m ? m[3] : '';
      if (!core) return part;

      const tokenOpts = {
        preserveAllCapsTokens,
        isTrailing: index === lastWordIdx,
      };
      return `${lead}${formatToken(core, tokenOpts)}${trail}`;
    })
    .join('');
}

/** Alias used by Vue templates that historically called titleCase(...). */
export function titleCase(value) {
  return formatPlaceTitle(value);
}
